import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Layers, Plus, X, UserPlus } from "lucide-react";
import { QuickContactModal } from "./QuickContactModal";

const CATEGORIES_EXPENSE = [
  "Aluguel do Templo",
  "Água / Luz / Internet / Telefone",
  "Impostos e Taxas",
  "Contribuição à Potência (GLESP/GOB)",
  "Manutenção Predial",
  "Materiais Ritualísticos",
  "Ágapes / Cerimônias",
  "Auxílios / Beneficência",
  "Softwares & Assinaturas",
  "Diversos / Outros",
];
const CATEGORIES_INCOME = [
  "Doação Pontual",
  "Festival / Evento Beneficente",
  "Contribuição Extraordinária",
  "Reembolso",
  "Diversos / Outros",
];
const FREQ_MONTHS = { monthly: 1, quarterly: 3, semester: 6, yearly: 12 };

export const InstallmentBatchForm = ({ onSuccess, onCancel, contacts = [], members = [], costCenters = [] }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [financeCategories, setFinanceCategories] = useState([]);
  const [isQuickContactOpen, setIsQuickContactOpen] = useState(false);
  const [localContacts, setLocalContacts] = useState(contacts);

  useEffect(() => { setLocalContacts(contacts); }, [contacts]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/finance/categories?active_only=true`);
        setFinanceCategories(r.data || []);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line
  }, []);

  const handleAddCategory = async () => {
    const name = window.prompt("Nova categoria para pagamentos e recebimentos:", "");
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API}/finance/categories`, {
        name: name.trim(),
        directions: ["receita", "despesa"],
        subcategories: [],
        is_active: true,
      });
      const r = await axios.get(`${API}/finance/categories?active_only=true`);
      setFinanceCategories(r.data || []);
      setForm(f => ({ ...f, category: name.trim() }));
      toast.success("Categoria criada.");
    } catch { toast.error("Erro ao criar categoria."); }
  };
  const [form, setForm] = useState({
    account_type: "conta_corrente_loja",
    type: "despesa",
    category: "Aluguel do Templo",
    subcategory: "",
    total_amount: "",
    installments: "1",
    occurrences: "1",
    frequency: "monthly",
    description: "",
    contact_id: "",
    member_id: "",
    cost_center_id: "",
    payment_method: "PIX",
    first_due_date: new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (financeCategories.length > 0 && !financeCategories.some((category) => category.name === form.category)) {
      setForm((current) => ({ ...current, category: financeCategories[0].name, subcategory: "" }));
    }
  }, [financeCategories, form.category]);

  const preview = useMemo(() => {
    const totalPerOcc = parseFloat(form.total_amount) || 0;
    const inst = Math.max(1, Math.min(12, parseInt(form.installments) || 1));
    const occ = Math.max(1, Math.min(60, parseInt(form.occurrences) || 1));
    if (totalPerOcc <= 0) return [];
    const per = Math.round((totalPerOcc / inst) * 100) / 100;
    const residual = Math.round((totalPerOcc - per * inst) * 100) / 100;
    const freqMonths = FREQ_MONTHS[form.frequency] || 1;
    try {
      const first = new Date(form.first_due_date + "T00:00");
      if (isNaN(first.getTime())) return [];
      const list = [];
      for (let o = 0; o < occ; o++) {
        for (let i = 0; i < inst; i++) {
          const d = new Date(first);
          d.setMonth(d.getMonth() + o * freqMonths + i);
          list.push({
            n: list.length + 1,
            occLabel: occ === 1 ? "" : `Ciclo ${o + 1}/${occ}`,
            instLabel: inst === 1 ? "" : `parc. ${i + 1}/${inst}`,
            due: d.toLocaleDateString("pt-BR"),
            amount: per + (i === inst - 1 ? residual : 0),
          });
          if (list.length >= 30) return list; // Limita prévia
        }
      }
      return list;
    } catch { return []; }
  }, [form.total_amount, form.installments, form.occurrences, form.frequency, form.first_due_date]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const selectedPerson = form.member_id
    ? `member:${form.member_id}`
    : form.contact_id
      ? `contact:${form.contact_id}`
      : "";

  const handlePersonChange = (value) => {
    if (!value) return set({ contact_id: "", member_id: "" });
    const [entityType, entityId] = value.split(":");
    set(entityType === "member"
      ? { member_id: entityId, contact_id: "" }
      : { contact_id: entityId, member_id: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const total = parseFloat(form.total_amount);
    if (!total || total <= 0) return toast.error("Informe um valor total maior que zero.");
    if (!form.description.trim()) return toast.error("Descrição é obrigatória.");
    if (!form.first_due_date) return toast.error("Data da 1ª parcela é obrigatória.");
    if (!form.contact_id && !form.member_id) return toast.error("Selecione um fornecedor, cliente ou Irmão.");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/finance/installments/batch`, {
        ...form,
        total_amount: total,
        installments: parseInt(form.installments) || 1,
        occurrences: parseInt(form.occurrences) || 1,
      });
      toast.success(`Cronograma criado: ${res.data.count} lançamento(s) — total R$ ${res.data.total_generated?.toFixed(2)}`);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar rateio.");
    } finally {
      setBusy(false);
    }
  };

  const fallbackCategories = [...new Set([...CATEGORIES_EXPENSE, ...CATEGORIES_INCOME])];
  const categories = financeCategories.length > 0
    ? financeCategories.filter((category) => category.is_active !== false).map((category) => category.name)
    : fallbackCategories;
  const selectedCategory = financeCategories.find((category) => category.name === form.category);
  const cls = "w-full bg-[#080C14] text-white p-2 rounded-lg border border-slate-700 focus:border-[#D4AF37] text-xs";
  const lbl = "block text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1";

  return (
    <div className="luxury-card p-5 border-l-4 border-l-[#D4AF37]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#D4AF37]" />
          <h3 className="font-serif font-bold text-white text-base">Novo Lançamento Recorrente / Parcelado</h3>
        </div>
        {onCancel && (
          <button onClick={onCancel} className="p-1 text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500 italic mb-4">
        Combina <b>recorrência</b> (mensal, trimestral, semestral, anual) com <b>parcelamento</b> dentro de cada ocorrência.
        Exemplos: água mensal por 24 meses → mensal, 1 parcela, 24 ocorrências. IPTU 5× ao ano, por 3 anos → anual, 5 parcelas, 3 ocorrências.
      </p>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Tipo</label>
          <select data-testid="batch-select-type" value={form.type} onChange={(e) => set({ type: e.target.value })} className={cls}>
            <option value="despesa">Despesa</option>
            <option value="receita">Receita</option>
          </select>
        </div>
        <div>
          <label className={lbl}>Conta</label>
          <select data-testid="batch-select-account" value={form.account_type} onChange={(e) => set({ account_type: e.target.value })} className={cls}>
            <option value="conta_corrente_loja">Conta Corrente da Loja</option>
            <option value="tronco_beneficencia">Tronco de Beneficência</option>
          </select>
        </div>
        <div>
          <label className={lbl}>
            Plano de Contas (Categoria){" "}
            <button type="button" onClick={handleAddCategory} className="text-[9px] text-[#D4AF37] hover:underline ml-1" data-testid="btn-add-category">
              + Nova
            </button>
          </label>
          <select data-testid="batch-select-category" value={form.category} onChange={(e) => set({ category: e.target.value })} className={cls}>
            {[...new Set(categories)].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Subcategoria (opcional)</label>
          <input data-testid="batch-input-subcategory" list="batch-subcategory-options" value={form.subcategory} onChange={(e) => set({ subcategory: e.target.value })} className={cls} placeholder="Escolha ou digite…" />
          <datalist id="batch-subcategory-options">
            {(selectedCategory?.subcategories || []).map((item) => <option key={item} value={item} />)}
          </datalist>
        </div>
        <div>
          <label className={lbl}>Valor por Ocorrência (R$)</label>
          <input data-testid="batch-input-total" type="number" step="0.01" min="0" value={form.total_amount} onChange={(e) => set({ total_amount: e.target.value })} className={`${cls} font-mono text-right`} required />
          <div className="text-[10px] text-slate-500 italic mt-1">
            Ex.: valor do IPTU do ano; ou valor de UMA conta de água.
          </div>
        </div>
        <div>
          <label className={lbl}>Frequência (repetir a cada)</label>
          <select data-testid="batch-select-frequency" value={form.frequency} onChange={(e) => set({ frequency: e.target.value })} className={cls}>
            <option value="monthly">Mensal (a cada 1 mês)</option>
            <option value="quarterly">Trimestral (a cada 3 meses)</option>
            <option value="semester">Semestral (a cada 6 meses)</option>
            <option value="yearly">Anual (a cada 12 meses)</option>
          </select>
        </div>
        <div>
          <label className={lbl}>Parcelas por Ocorrência</label>
          <input data-testid="batch-input-installments" type="number" min="1" max="12" value={form.installments} onChange={(e) => set({ installments: e.target.value })} className={`${cls} font-mono text-right`} required />
          <div className="text-[10px] text-slate-500 italic mt-1">
            Ex.: IPTU pago em 5 parcelas → 5. Conta de água à vista → 1.
          </div>
        </div>
        <div>
          <label className={lbl}>Nº de Ocorrências</label>
          <input data-testid="batch-input-occurrences" type="number" min="1" max="60" value={form.occurrences} onChange={(e) => set({ occurrences: e.target.value })} className={`${cls} font-mono text-right`} required />
          <div className="text-[10px] text-slate-500 italic mt-1">
            Ex.: água por 2 anos (mensal) → 24; IPTU por 3 anos (anual) → 3.
          </div>
        </div>
        <div>
          <label className={lbl}>Vencimento da 1ª Parcela</label>
          <input data-testid="batch-input-first-due" type="date" value={form.first_due_date} onChange={(e) => set({ first_due_date: e.target.value })} className={cls} required />
        </div>
        <div>
          <label className={lbl}>Forma de Pagamento</label>
          <select data-testid="batch-select-payment-method" value={form.payment_method} onChange={(e) => set({ payment_method: e.target.value })} className={cls}>
            <option>A definir</option>
            <option>PIX</option>
            <option>Boleto</option>
            <option>Dinheiro</option>
            <option>Transferência TED</option>
          </select>
        </div>
        <div>
          <label className={lbl}>Centro de Custo</label>
          <select
            data-testid="batch-select-cost-center"
            value={form.cost_center_id}
            onChange={(e) => set({ cost_center_id: e.target.value })}
            className={cls}
          >
            <option value="">— Nenhum —</option>
            {costCenters.filter((cc) => cc.is_active !== false).map((cc) => (
              <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={lbl}>Pessoa Vinculada — Fornecedor, Cliente ou Irmão *</label>
          <div className="flex gap-2">
            <select
              data-testid="batch-select-person"
              value={selectedPerson}
              onChange={(e) => handlePersonChange(e.target.value)}
              className={`${cls} flex-1`}
            >
              <option value="">— Nenhuma —</option>
              {localContacts.some((c) => c.kind === "fornecedor") && (
                <optgroup label="Fornecedores">
                  {localContacts.filter((c) => c.kind === "fornecedor").map((c) => (
                    <option key={c.id} value={`contact:${c.id}`}>{c.name}</option>
                  ))}
                </optgroup>
              )}
              {localContacts.some((c) => c.kind === "cliente") && (
                <optgroup label="Clientes">
                  {localContacts.filter((c) => c.kind === "cliente").map((c) => (
                    <option key={c.id} value={`contact:${c.id}`}>{c.name}</option>
                  ))}
                </optgroup>
              )}
              {members.length > 0 && (
                <optgroup label="Irmãos">
                  {members.map((member) => (
                    <option key={member.id} value={`member:${member.id}`}>
                      {member.name} (CIM {member.cim})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              type="button"
              data-testid="btn-batch-quick-contact"
              onClick={() => setIsQuickContactOpen(true)}
              title="Cadastrar fornecedor/cliente ou selecionar Irmão"
              className="shrink-0 bg-[#D4AF37]/20 hover:bg-[#D4AF37]/40 text-[#D4AF37] rounded-xl px-3 border border-[#D4AF37]/40 flex items-center gap-1"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span className="text-[10px] uppercase font-bold tracking-wider">Novo / Selecionar</span>
            </button>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className={lbl}>Descrição / Histórico</label>
          <input data-testid="batch-input-description" value={form.description} onChange={(e) => set({ description: e.target.value })} className={cls} placeholder="ex.: Aluguel do Templo — Janeiro a Junho/2027" required />
          <div className="text-[10px] text-slate-500 italic mt-1">
            O texto "parcela X/N" é acrescentado automaticamente em cada lançamento.
          </div>
        </div>

        {preview.length > 0 && (
          <div className="md:col-span-2 p-3 bg-[#080C14] rounded-lg border border-slate-800">
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-2 flex items-center gap-1">
              <CalendarClock className="w-3 h-3" />
              Prévia — {preview.length} lançamento(s) {preview.length >= 30 ? "(mostrando os 30 primeiros)" : ""}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5 text-[10px] font-mono max-h-52 overflow-y-auto">
              {preview.map((p) => (
                <div key={p.n} className="flex justify-between p-1.5 bg-[#131C2E] rounded">
                  <span className="text-slate-400 truncate">
                    #{p.n} • {p.due}
                    {p.occLabel && <span className="text-[9px] text-[#D4AF37] ml-1">{p.occLabel}</span>}
                    {p.instLabel && <span className="text-[9px] text-slate-500 ml-1">{p.instLabel}</span>}
                  </span>
                  <span className="text-emerald-400 font-bold whitespace-nowrap">R$ {p.amount.toFixed(2).replace(".", ",")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="md:col-span-2 flex justify-end gap-2 mt-2">
          {onCancel && (
            <button type="button" onClick={onCancel} className="text-slate-400 hover:text-white text-xs uppercase font-bold px-4 py-2">
              Cancelar
            </button>
          )}
          <button
            data-testid="btn-submit-batch"
            type="submit"
            disabled={busy}
            className="flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-5 py-2 rounded-xl text-xs uppercase tracking-wider disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5" />
            Gerar {preview.length || "N"} Lançamento(s)
          </button>
        </div>
      </form>

      <QuickContactModal
        isOpen={isQuickContactOpen}
        onClose={() => setIsQuickContactOpen(false)}
        defaultKind={form.type === "receita" ? "cliente" : "fornecedor"}
        members={members}
        onCreated={(entity) => {
          if (entity.entity_type === "member") {
            set({ member_id: entity.id, contact_id: "" });
          } else {
            setLocalContacts((prev) => [...prev, entity]);
            set({ contact_id: entity.id, member_id: "" });
          }
        }}
      />
    </div>
  );
};
